import api from './api';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  status: 'unread' | 'read';
  priority: 'low' | 'medium' | 'high';
  recipient: string;
  createdAt: string;
  readAt?: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  highPriority: number;
}

export interface NotificationsResponse {
  success: boolean;
  data: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

class NotificationsService {
  /**
   * Get all notifications
   */
  async getNotifications(params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    priority?: string;
    search?: string;
  }): Promise<NotificationsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.type) queryParams.append('type', params.type);
      if (params?.priority) queryParams.append('priority', params.priority);
      if (params?.search) queryParams.append('search', params.search);

      const response = await api.get(`/admin/notifications?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get notifications error:', error);
      throw error;
    }
  }

  /**
   * Get notification stats
   */
  async getStats(): Promise<{ success: boolean; data: NotificationStats }> {
    const response = await api.get('/admin/notifications/stats');
    return response.data;
  }

  /**
   * Get notification by ID
   */
  async getNotification(id: string): Promise<{ success: boolean; data: Notification }> {
    try {
      const response = await api.get(`/admin/notifications/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get notification error:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/notifications/${id}/read`);
      return response.data;
    } catch (error) {
      console.error('Mark as read error:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put('/admin/notifications/read-all');
      return response.data;
    } catch (error) {
      console.error('Mark all as read error:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/notifications/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete notification error:', error);
      throw error;
    }
  }

  /**
   * Create notification
   */
  async createNotification(data: {
    title: string;
    message: string;
    type?: 'info' | 'warning' | 'error' | 'success';
    priority?: 'low' | 'medium' | 'high';
    clienteId?: string;
    userId?: string;
  }): Promise<{ success: boolean; data: { id: string }; message: string }> {
    try {
      const response = await api.post('/admin/notifications', data);
      return response.data;
    } catch (error) {
      console.error('Create notification error:', error);
      throw error;
    }
  }

  /** Push FCM para todos os dispositivos da app (dryRun só conta) */
  async pushBroadcast(data: {
    title: string;
    body: string;
    app?: 'pagali' | 'pagali_shop' | 'pass' | 'pos' | 'todas';
    url?: string;
    dryRun?: boolean;
  }): Promise<{ success: boolean; data: { total: number; sent?: number; failed?: number; dryRun?: boolean } }> {
    const response = await api.post('/admin/notifications/push-broadcast', data);
    return response.data;
  }

  /**
   * Setup notifications table
   */
  async setupTable(): Promise<{ success: boolean; message: string; created: boolean }> {
    try {
      const response = await api.post('/admin/notifications/setup');
      return response.data;
    } catch (error) {
      console.error('Setup table error:', error);
      throw error;
    }
  }
}

export default new NotificationsService();
